import { client } from '@api/client';
import { URL } from '@api/urls';

import { TIdName, TNameTitle } from '@interfaces/common/common';
import { IGroupOperationDTO } from '@interfaces/common/defaultDTO';
import { IItemsResponse } from '@interfaces/common/response';
import {
	ICheckProjectCodeDTO,
	ICreateProjectDTO,
	IDeleteProjectsDTO,
	IEditProjectDTO,
	IEditProjectStatusDTO,
	IGetPortalProjectByIdDTO,
	IGetPortalProjectsListDTO,
	IGetProjectsListDTO,
	IProjectDTO,
} from '@interfaces/portal/dto/projectManagementDTO';
import { IProjectUser, IProjectWorkflow } from '@interfaces/portal/projects';

const {
	platformOwnerProjects,
	platformOwnerProjectsStatuses,
	platformOwnerProjectsCreate,
	platformOwnerProjectsWorkflows,
	platformOwnerProjectsAdmins,
	platformOwnerProjectsMembers,
	platformOwnerProjectsDelete,
	organisationProjects,
	organisationProjectsStatuses,
	organisationProjectsCreate,
	organisationProjectsWorkflows,
	organisationProjectsAdmins,
	organisationProjectsMembers,
	organisationProjectsDelete,
	portalProjects,
	portalProjectsStatuses,
	portalProjectAdmins,
	portalProjectMembers,
	portalAvailableProjects,
} = URL;

export const getProjectsList = ({
	page,
	pageSize,
	sortDirection,
	sortFieldName,
	searchValue,
	organizationId,
	status,
}: IGetProjectsListDTO): Promise<IItemsResponse<IProjectDTO>> => {
	return client.get(platformOwnerProjects, {
		params: {
			page,
			pageSize,
			sortDirection,
			sortFieldName,
			searchValue,
			organizationId,
			status,
		},
	});
};

export const createProject = (data: ICreateProjectDTO): Promise<IProjectDTO> => {
	return client.post(platformOwnerProjectsCreate, data);
};

export const editProject = ({ projectId, dto }: IEditProjectDTO): Promise<IProjectDTO> => {
	return client.post(`${platformOwnerProjects}/${projectId}/update`, {
		...dto,
	});
};

export const getProjectsStatuses = (): Promise<TNameTitle[]> => {
	return client.get(platformOwnerProjectsStatuses);
};

export const getProjectWorkflows = (): Promise<IProjectWorkflow[]> => {
	return client.get(platformOwnerProjectsWorkflows);
};

export const getProjectAdmins = ({ organizationId }: { organizationId: string }): Promise<IProjectUser[]> => {
	return client.get(platformOwnerProjectsAdmins, {
		params: {
			organizationId,
		},
	});
};

export const getProjectMembers = ({ organizationId }: { organizationId: string }): Promise<IProjectUser[]> => {
	return client.get(platformOwnerProjectsMembers, {
		params: {
			organizationId,
		},
	});
};

export const removeProjects = ({
	filter,
	ids,
	isAll,
}: IGroupOperationDTO<IDeleteProjectsDTO | null>): Promise<void> => {
	return client.post(platformOwnerProjectsDelete, {
		filter,
		ids,
		isAll,
	});
};

export const checkProjectCode = ({ code, organizationId }: ICheckProjectCodeDTO): Promise<void> => {
	return client.post(`${platformOwnerProjects}/check-code`, {
		code,
		organizationId,
	});
};

export const getOrganisationProjectsList = ({
	page,
	pageSize,
	sortDirection,
	sortFieldName,
	searchValue,
	organizationId,
	status,
}: IGetProjectsListDTO): Promise<IItemsResponse<IProjectDTO>> => {
	return client.get(organisationProjects, {
		params: {
			page,
			pageSize,
			sortDirection,
			sortFieldName,
			searchValue,
			organizationId,
			status,
		},
	});
};

export const createOrganisationProject = (data: ICreateProjectDTO): Promise<IProjectDTO> => {
	return client.post(organisationProjectsCreate, data);
};

export const editOrganisationProject = ({ projectId, dto }: IEditProjectDTO): Promise<IProjectDTO> => {
	return client.post(`${organisationProjects}/${projectId}/update`, {
		...dto,
	});
};

export const getOrganisationProjectsStatuses = (): Promise<TNameTitle[]> => {
	return client.get(organisationProjectsStatuses);
};

export const getOrganisationProjectWorkflows = (): Promise<IProjectWorkflow[]> => {
	return client.get(organisationProjectsWorkflows);
};

export const getOrganisationProjectAdmins = ({
	organizationId,
}: { organizationId: string }): Promise<IProjectUser[]> => {
	return client.get(organisationProjectsAdmins, {
		params: {
			organizationId,
		},
	});
};

export const getOrganisationProjectMembers = ({
	organizationId,
}: { organizationId: string }): Promise<IProjectUser[]> => {
	return client.get(organisationProjectsMembers, {
		params: {
			organizationId,
		},
	});
};

export const removeOrganisationProjects = ({
	filter,
	ids,
	isAll,
}: IGroupOperationDTO<IDeleteProjectsDTO | null>): Promise<void> => {
	return client.post(organisationProjectsDelete, {
		filter,
		ids,
		isAll,
	});
};

export const checkOrganisationProjectCode = ({ code, organizationId }: ICheckProjectCodeDTO): Promise<void> => {
	return client.post(`${organisationProjects}/check-code`, {
		code,
		organizationId,
	});
};

export const getPortalProjectsList = ({
	page,
	pageSize,
	sortDirection,
	sortFieldName,
	searchValue,
	status,
	organizationId,
}: IGetPortalProjectsListDTO): Promise<IItemsResponse<IProjectDTO>> => {
	return client.get(portalProjects, {
		params: {
			page,
			pageSize,
			sortDirection,
			sortFieldName,
			searchValue,
			status,
			organizationId,
		},
	});
};

export const getPortalProjectsStatuses = (): Promise<TNameTitle[]> => {
	return client.get(portalProjectsStatuses);
};

export const getPortalProjectById = ({ id, organizationId }: IGetPortalProjectByIdDTO): Promise<IProjectDTO> => {
	return client.get(`${portalProjects}/${id}`, {
		params: { organizationId },
	});
};

export const changePortalProjectStatus = ({ projectId, dto }: IEditProjectStatusDTO): Promise<IProjectDTO> => {
	return client.post(`${portalProjects}/${projectId}/update-status`, {
		...dto,
	});
};

export const editPortalProject = ({ projectId, dto }: IEditProjectDTO): Promise<IProjectDTO> => {
	return client.post(`${portalProjects}/${projectId}/update`, {
		...dto,
	});
};

export const getPortalProjectAdmins = ({ organizationId }: { organizationId: string }): Promise<IProjectUser[]> => {
	return client.get(portalProjectAdmins, {
		params: {
			organizationId,
		},
	});
};

export const getPortalProjectMembers = ({ organizationId }: { organizationId: string }): Promise<IProjectUser[]> => {
	return client.get(portalProjectMembers, {
		params: {
			organizationId,
		},
	});
};

export const getPortalAvailableProjects = (): Promise<TIdName[]> => {
	return client.get(portalAvailableProjects);
};
